import React, { useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Chip,
  Paper
} from '@material-ui/core';
import {
  Person as PersonIcon,
  Event as EventIcon,
  AttachMoney as MoneyIcon,
  DirectionsBike as BikeIcon,
  GpsFixed as GpsIcon,
  Hotel as HotelIcon,
  Security as SecurityIcon
} from '@material-ui/icons';
import styled from 'styled-components';
import moment from 'moment';
import TourCalculationService from '../../services/TourCalculationService';
import tourPricingService from '../../services/tourPricingService';

const StyledCard = styled(Card)`
  margin-bottom: 16px;
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e0e0e0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const SummaryLabel = styled.div`
  font-weight: 500;
  color: #333;
  display: flex;
  align-items: center;
  
  .MuiSvgIcon-root {
    margin-right: 8px;
    font-size: 16px;
  }
`;

const SummaryValue = styled.div`
  font-weight: 600;
  color: #1976d2;
`;

const PriceBreakdown = styled.div`
  background-color: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  margin-top: 16px;
`;

const PriceItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
    border-top: 1px solid #e0e0e0;
    padding-top: 8px;
    font-weight: bold;
  }
`;

const CorrectionHighlight = styled.span`
  color: #f57c00;
  font-weight: bold;
`;

/**
 * Componente para mostrar el resumen del tour con cálculos corregidos
 * Basado en la primera imagen del Tour Summary
 */
const TourSummary = ({ tourData, pricing, showCorrections = true }) => {
  // Calcular días efectivos correctos
  const effectiveDays = useMemo(() => {
    if (!tourData || !tourData.booking || !tourData.booking.tour) return 0;
    
    return TourCalculationService.calculateEffectiveDays(
      tourData.booking.tour.effective_start_date,
      tourData.booking.tour.effective_end_date
    );
  }, [tourData]);

  // Calcular precios corregidos
  const correctedPricing = useMemo(() => {
    if (!pricing || !pricing.breakdown) return null;

    const breakdown = pricing.breakdown;
    const corrections = {};

    // Corregir GPS si es necesario
    if (breakdown.gpsPrice > 0 && effectiveDays !== breakdown.effectiveDays) {
      const dailyGpsRate = breakdown.gpsPrice / breakdown.effectiveDays;
      corrections.gps = {
        original: breakdown.gpsPrice,
        corrected: dailyGpsRate * effectiveDays,
        dailyRate: dailyGpsRate,
        days: effectiveDays
      };
    }

    // Corregir seguro VIP si es necesario
    if (breakdown.vipInsurancePrice > 0 && effectiveDays !== breakdown.effectiveDays) {
      const dailyInsuranceRate = breakdown.vipInsurancePrice / breakdown.effectiveDays;
      corrections.vipInsurance = {
        original: breakdown.vipInsurancePrice,
        corrected: dailyInsuranceRate * effectiveDays,
        dailyRate: dailyInsuranceRate,
        days: effectiveDays
      };
    }

    return corrections;
  }, [pricing, effectiveDays]);

  // Calcular totales usando la lógica exacta del backend
  const totals = useMemo(() => {
    const exactTotals = tourPricingService.calculateTotalsExact({ tourData, pricing });
    
    
    return {
      basePrice: exactTotals.breakdown.tourPrice1 || 0,
      passengerPrice: exactTotals.breakdown.passengerPrice || 0,
      gpsPrice: exactTotals.breakdown.gpsPrice || 0,
      total: exactTotals.total1 || 0,
      total1: exactTotals.total1 || 0,
      total2: exactTotals.total2 || 0,
      breakdown: exactTotals.breakdown
    };
  }, [tourData, pricing]);

  // Formatear moneda
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  // Formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return moment(dateString).format('DD/MM/YYYY');
  };

  if (!tourData) {
    return (
      <StyledCard>
        <CardContent>
          <Typography variant="body2" color="textSecondary">
            No hay datos del tour disponibles
          </Typography>
        </CardContent>
      </StyledCard>
    );
  }

  const booking = tourData.booking;
  const tour = booking && booking.tour;
  const driver = tourData.driver;

  return (
    <StyledCard>
      <CardContent>
        <Typography variant="h6" gutterBottom style={{ color: '#f57c00' }}>
          Tour Summary
        </Typography>

        {/* Información básica del tour */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <SummaryItem>
              <SummaryLabel>
                <EventIcon />
                Booking Date
              </SummaryLabel>
              <SummaryValue>
                {moment().format('DD/MM/YYYY HH:mm:ss')}
              </SummaryValue>
            </SummaryItem>

            <SummaryItem>
              <SummaryLabel>
                <EventIcon />
                Tour
              </SummaryLabel>
              <SummaryValue>
                {(tour && tour.tour_name) || 'N/A'}
              </SummaryValue>
            </SummaryItem>

            <SummaryItem>
              <SummaryLabel>
                <EventIcon />
                Tour Start Date
              </SummaryLabel>
              <SummaryValue>
                {formatDate(tour && tour.tour_start_date)}
              </SummaryValue>
            </SummaryItem>

            <SummaryItem>
              <SummaryLabel>
                <EventIcon />
                Tour End Date
              </SummaryLabel>
              <SummaryValue>
                {formatDate(tour && tour.tour_end_date)}
              </SummaryValue>
            </SummaryItem>

            <SummaryItem>
              <SummaryLabel>
                <PersonIcon />
                Rider days
              </SummaryLabel>
              <SummaryValue>
                {effectiveDays} days
              </SummaryValue>
            </SummaryItem>
          </Grid>

          <Grid item xs={12} md={6}>
            <SummaryItem>
              <SummaryLabel>
                <PersonIcon />
                Passenger
              </SummaryLabel>
              <SummaryValue>
                {(booking && booking.p_first_name) || 'N/A'}
              </SummaryValue>
            </SummaryItem>

            {booking && booking.roommate && booking.roommate !== 'No' && (
              <SummaryItem>
                <SummaryLabel>
                  <HotelIcon />
                  Roommate name
                </SummaryLabel>
                <SummaryValue>
                  {booking.roommate}
                </SummaryValue>
              </SummaryItem>
            )}

            <SummaryItem>
              <SummaryLabel>
                <BikeIcon />
                First Choice Motorcycle
              </SummaryLabel>
              <SummaryValue>
                F900GS ADV
              </SummaryValue>
            </SummaryItem>

            <SummaryItem>
              <SummaryLabel>
                <BikeIcon />
                Second Choice Motorcycle
              </SummaryLabel>
              <SummaryValue>
                R1250GS
              </SummaryValue>
            </SummaryItem>

            <SummaryItem>
              <SummaryLabel>
                <GpsIcon />
                GPS
              </SummaryLabel>
              <SummaryValue>
                {(booking && booking.r_gps) ? 'Yes' : 'No'}
              </SummaryValue>
            </SummaryItem>
          </Grid>
        </Grid>

        <Divider style={{ margin: '16px 0' }} />

        {/* Desglose de precios */}
        <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 'bold' }}>
          Cost Breakdown
        </Typography>

        <PriceBreakdown>
                     <PriceItem>
             <span>Cost for first choice motorcycle:</span>
             <span>{formatCurrency(totals.basePrice)}</span>
           </PriceItem>
           
           <PriceItem>
             <span>Cost for second choice motorcycle:</span>
             <span>{formatCurrency(totals.basePrice)}</span>
           </PriceItem>
           
           <PriceItem>
             <span>First choice of motorcycle:</span>
             <span>{formatCurrency(totals.total1)}</span>
           </PriceItem>
           
           <PriceItem>
             <span>Second choice of motorcycle:</span>
             <span>{formatCurrency(totals.total2)}</span>
           </PriceItem>
          
          <PriceItem>
            <span>
              GPS Device
              {correctedPricing && correctedPricing.gps && showCorrections && (
                <CorrectionHighlight>
                  {" "}(€{correctedPricing.gps.dailyRate.toFixed(2)}/day × {correctedPricing.gps.days} days)
                </CorrectionHighlight>
              )}
            </span>
                         <span>
               {correctedPricing && correctedPricing.gps && showCorrections ? (
                 <CorrectionHighlight>
                   {formatCurrency(correctedPricing.gps.corrected)}
                 </CorrectionHighlight>
               ) : (
                 formatCurrency(totals.gpsPrice)
               )}
             </span>
          </PriceItem>
          
          <PriceItem>
            <span>Rewards Used:</span>
            <span>0 points</span>
          </PriceItem>
          
                     <PriceItem>
             <span>Total Price:</span>
             <span style={{ fontWeight: 'bold', fontSize: '1.1em' }}>
               {formatCurrency(totals.total1)}
             </span>
           </PriceItem>
        </PriceBreakdown>

        {/* Información adicional */}
        <Grid container spacing={2} style={{ marginTop: 16 }}>
          <Grid item xs={12} md={6}>
            <SummaryItem>
              <SummaryLabel>
                <SecurityIcon />
                Client Passport
              </SummaryLabel>
              <SummaryValue>N/A</SummaryValue>
            </SummaryItem>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <SummaryItem>
              <SummaryLabel>
                <SecurityIcon />
                Driving Licence
              </SummaryLabel>
              <SummaryValue>N/A</SummaryValue>
            </SummaryItem>
          </Grid>
        </Grid>

        {/* Mostrar correcciones si están habilitadas */}
        {showCorrections && correctedPricing && Object.keys(correctedPricing).length > 0 && (
          <Paper style={{ marginTop: 16, padding: 16, backgroundColor: '#fff3e0' }}>
            <Typography variant="subtitle2" style={{ color: '#f57c00', marginBottom: 8 }}>
              Correcciones Aplicadas:
            </Typography>
            
            {correctedPricing.gps && (
              <Typography variant="body2" style={{ marginBottom: 4 }}>
                • GPS: {formatCurrency(correctedPricing.gps.original)} → {formatCurrency(correctedPricing.gps.corrected)}
                {" "}({correctedPricing.gps.days} días efectivos)
              </Typography>
            )}
            
            {correctedPricing.vipInsurance && (
              <Typography variant="body2">
                • VIP Insurance: {formatCurrency(correctedPricing.vipInsurance.original)} → {formatCurrency(correctedPricing.vipInsurance.corrected)}
                {" "}({correctedPricing.vipInsurance.days} días efectivos)
              </Typography>
            )}
          </Paper>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default TourSummary; 


// WEBPACK FOOTER //
// ./src/components/tour/TourSummary.js